import { doc, getDoc, setDoc, updateDoc, collection, getDocs, query, where, serverTimestamp } from 'firebase/firestore';
import { firebaseFirestore } from '@/infrastructure/firebase/firebaseFirestore.js';
import { GlobalRole, UserStatus, type UserProfile } from '@k-ucap/shared';
import { getCurrentEnvironment, buildConverter, withTimestamp, toTimestampString } from './firestoreUtils.js';

const USERS = 'users';

const userConverter = buildConverter<UserProfile>(
  (data) =>
    ({
      uid: data.uid,
      email: data.email,
      displayName: data.displayName,
      photoURL: data.photoURL ?? undefined,
      globalRole: data.globalRole,
      status: data.status,
      organization: data.organization ?? undefined,
      department: data.department ?? undefined,
      phoneNumber: data.phoneNumber ?? undefined,
      defaultProjectId: data.defaultProjectId ?? undefined,
      authProvider: data.authProvider,
      environment: data.environment,
      lastLoginAt: toTimestampString(data.lastLoginAt) ?? undefined,
      membershipIds: data.membershipIds ?? undefined,
      createdAt: toTimestampString(data.createdAt) ?? new Date().toISOString(),
      updatedAt: toTimestampString(data.updatedAt) ?? new Date().toISOString(),
      createdBy: data.createdBy ?? undefined,
    }) as UserProfile,
  (model) => ({
    ...model,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
);

export class FirestoreUserRepository {
  async findById(uid: string): Promise<UserProfile | undefined> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const snap = await getDoc(doc(db, USERS, uid).withConverter(userConverter));
    return snap.data();
  }

  async findByEmail(email: string): Promise<UserProfile | undefined> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, USERS),
      where('email', '==', email),
      where('environment', '==', getCurrentEnvironment())
    ).withConverter(userConverter);
    const snap = await getDocs(q);
    return snap.docs[0]?.data();
  }

  async findAll(): Promise<UserProfile[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(collection(db, USERS), where('environment', '==', getCurrentEnvironment())).withConverter(
      userConverter
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async save(profile: UserProfile): Promise<UserProfile> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const existing = await this.findById(profile.uid);
    const data = {
      ...profile,
      environment: getCurrentEnvironment(),
    };
    if (existing) {
      await updateDoc(doc(db, USERS, profile.uid), {
        ...data,
        updatedAt: serverTimestamp(),
      });
    } else {
      await setDoc(doc(db, USERS, profile.uid).withConverter(userConverter), withTimestamp(data, true) as UserProfile);
    }
    return { ...data, createdAt: existing?.createdAt ?? new Date().toISOString(), updatedAt: new Date().toISOString() };
  }

  async updateDefaultProject(uid: string, defaultProjectId: string): Promise<void> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    await updateDoc(doc(db, USERS, uid), { defaultProjectId, updatedAt: serverTimestamp() });
  }

  async ensureDefaultProfile(
    authUser: { uid: string; email: string | null; displayName: string | null; photoURL: string | null },
    authProvider: 'email' | 'google' | 'mock'
  ): Promise<UserProfile> {
    const existing = await this.findById(authUser.uid);
    if (existing) return existing;
    const now = new Date().toISOString();
    const profile: UserProfile = {
      uid: authUser.uid,
      email: authUser.email ?? '',
      displayName: authUser.displayName ?? authUser.email ?? 'Unknown',
      photoURL: authUser.photoURL ?? undefined,
      globalRole: GlobalRole.GUEST,
      status: UserStatus.ACTIVE,
      authProvider,
      environment: getCurrentEnvironment(),
      lastLoginAt: now,
      createdAt: now,
      updatedAt: now,
      createdBy: authUser.uid,
    };
    await this.save(profile);
    return profile;
  }
}

export const createFirestoreUserRepository = (): FirestoreUserRepository => new FirestoreUserRepository();
